<template>
  <div class="rms-day-price-chart-wrap">
    <div class="rms-day-price-chart-current">
      <div
        class="rms-day-price-chart-current__label caption grey--text text-uppercase"
      >
        {{ $t("current_price") }}
      </div>
      <div class="rms-day-price-chart-current__value">
        {{ current_price }}{{ currency_symbol }}
      </div>
    </div>
    <zingchart
      :height="height"
      :width="width"
      :data="graph_data"
      :series="graph_series"
      :id="id"
    ></zingchart>
  </div>
</template>
<script>
import Vue from "vue";
import { mapState, mapGetters } from "vuex";

var RN_GREEN = "#66BB6A";
var RN_RED = "#E53935";
var PRICE_LINE_NEUTRAL = "#616161";
var WEEKEND_BAND = "#fbf9ef";

export default {
  name: "a-rms-day-price-evolution-chart",
  props: {
    id: {
      type: String,
      default: "a-rms-day-price-evolution-chart"
    },
    height: {
      type: String,
      default: "520px"
    },
    width: {
      type: String,
      default: "100%"
    },
    current_price: {
      type: Number,
      default: 0
    },
    series: {
      type: Object,
      default: function() {
        return {};
      }
    }
  },
  computed: {
    currency_symbol() {
      return this.current_hotel && this.current_hotel.currency_symbol
        ? this.current_hotel.currency_symbol
        : "\u20AC";
    },
    evolution() {
      return this.series.price_evolution || [];
    },
    prices() {
      return this.evolution.map(function(p) {
        return p.pvp;
      });
    },
    /** Segment i→i+1 usa l'estil del node i+1 (ZingChart). */
    price_line_styles() {
      var prices = this.prices;
      var styles = [];
      var flat = { "line-color": PRICE_LINE_NEUTRAL };
      for (var i = 0; i < prices.length; i++) {
        if (i === 0) {
          styles.push(flat);
          continue;
        }
        var prev = prices[i - 1];
        var curr = prices[i];
        if (
          curr === null ||
          curr === undefined ||
          prev === null ||
          prev === undefined
        ) {
          styles.push(flat);
          continue;
        }
        if (curr > prev) styles.push({ "line-color": RN_GREEN });
        else if (curr < prev) styles.push({ "line-color": RN_RED });
        else styles.push(flat);
      }
      return styles;
    },
    rn_values() {
      return this.evolution.map(function(p) {
        return p.rn !== undefined ? p.rn : 0;
      });
    },
    rn_positive_values() {
      return this.rn_values.map(function(v) {
        if (v === null || v === undefined) return null;
        return v > 0 ? v : null;
      });
    },
    rn_negative_values() {
      return this.rn_values.map(function(v) {
        if (v === null || v === undefined) return null;
        return v < 0 ? v : null;
      });
    },
    occupancy_values() {
      return this.evolution.map(function(p) {
        return p.occupancy !== undefined ? p.occupancy : null;
      });
    },
    rn_scale_range() {
      var vals = this.rn_values.filter(function(v) {
        return v !== null && v !== undefined;
      });
      if (vals.length === 0) return { min: 0, max: 10, step: 2 };
      var dataMin = Math.min.apply(null, vals);
      var dataMax = Math.max.apply(null, vals);
      var rnMin = Math.min(0, dataMin);
      var span = dataMax - dataMin;
      var pad = Math.max(span * 0.12, 1);
      // Marge extra per sobre: les barres i l'ocupació queden a la part baixa del gràfic
      var headroom = Math.max(span * 0.85, Math.abs(dataMax) * 0.65 + 6, 14);
      var rnMax = dataMax + pad + headroom;
      if (rnMax <= rnMin) rnMax = rnMin + 10;
      var step = Math.ceil((rnMax - rnMin) / 5);
      if (step < 1) step = 1;
      rnMax = rnMin + step * 5;
      return { min: rnMin, max: rnMax, step: step };
    },
    occupancy_mapped() {
      var r = this.rn_scale_range;
      var self = this;
      return this.occupancy_values.map(function(occ) {
        if (occ === null || occ === undefined) return null;
        // return r.min + (occ / 100) * (r.max - r.min);
        return (occ / 100) * r.max;
      });
    },
    price_range() {
      if (this.prices.length === 0) return { min: 0, max: 100, step: 20 };
      var valid = this.prices.filter(function(v) {
        return v !== null;
      });
      if (valid.length === 0) return { min: 0, max: 100, step: 20 };
      var min = Math.min.apply(null, valid);
      var max = Math.max.apply(null, valid);
      var paddingTop = Math.max((max - min) * 0.12, 5);
      // Marge extra per sota: el preu es dibuixa a la part alta de l'eix
      var paddingBottom = Math.max((max - min) * 0.92, min * 0.22, 50);
      var step = Math.ceil((max - min + paddingTop + paddingBottom) / 5);
      if (step < 1) step = 1;
      var scaleMin = Math.floor(min - paddingBottom);
      var scaleMax = scaleMin + step * 5;
      return { min: scaleMin, max: scaleMax, step: step };
    },
    chart_labels() {
      return this.evolution.map(function(point) {
        return "D-" + point.days_before;
      });
    },
    visible_labels() {
      var total = this.chart_labels.length;
      if (total <= 15) return {};
      var step = Math.ceil(total / 10);
      var labels = {};
      var milestones = [90, 60, 30, 14, 7, 3, 1, 0];
      for (var i = 0; i < total; i++) {
        var days = this.evolution[i] && this.evolution[i].days_before;
        if (milestones.indexOf(days) !== -1) {
          labels[i] = true;
        } else if (i % step === 0) {
          labels[i] = true;
        }
      }
      labels[0] = true;
      labels[total - 1] = true;
      return labels;
    },
    formatted_labels() {
      var self = this;
      return this.chart_labels.map(function(label, i) {
        if (Object.keys(self.visible_labels).length === 0) return label;
        return self.visible_labels[i] ? label : "";
      });
    },
    weekend_scale_markers() {
      var ev = this.evolution;
      if (!ev.length) return [];
      var ranges = [];
      var start = null;
      function isWeekend(dateStr) {
        if (!dateStr) return false;
        var d = Vue.moment(dateStr, "YYYY-MM-DD").day();
        return d === 0 || d === 5 || d === 6;
      }
      for (var i = 0; i < ev.length; i++) {
        var w = isWeekend(ev[i].date);
        if (w) {
          if (start === null) start = i;
        } else {
          if (start !== null) {
            ranges.push([start, i - 1]);
            start = null;
          }
        }
      }
      if (start !== null) ranges.push([start, ev.length - 1]);
      return ranges.map(function(r) {
        return {
          type: "area",
          range: r,
          backgroundColor: WEEKEND_BAND,
          alpha: 1
        };
      });
    },
    graph_data() {
      var range = this.price_range;
      var rnR = this.rn_scale_range;
      var scaleColor = this.chart_settings.globals.scaleColor;
      var sx = {
        lineWidth: 0,
        lineColor: scaleColor,
        labels: this.formatted_labels,
        item: {
          paddingTop: "10px",
          color: "#333333",
          fontSize: "12px",
          fontFamily: "Roboto Condensed"
        },
        tick: {
          size: 0
        }
      };
      if (this.weekend_scale_markers.length) {
        sx.markers = this.weekend_scale_markers;
      }
      return {
        type: "mixed",
        globals: this.chart_settings.globals,
        gui: this.chart_settings.gui,
        backgroundColor: "none",
        legend: {
          visible: true,
          borderWidth: 0,
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom",
          marginTop: "8px",
          marginBottom: "4px",
          toggleAction: "none",
          item: {
            fontSize: "11px",
            fontFamily: "Roboto Condensed",
            fontColor: "#333333"
          },
          marker: {
            type: "circle",
            size: 5
          }
        },
        plotarea: {
          margin: "52px 72px 64px 70px"
        },
        tooltip: {
          visible: false
        },
        scaleX: sx,
        scaleY: {
          lineWidth: 0,
          values: range.min + ":" + range.max + ":" + range.step,
          format: "%v" + this.currency_symbol,
          guide: {
            lineStyle: "solid",
            lineColor: "#f5f5f5"
          },
          tick: {
            size: 0
          },
          item: {
            visible: true,
            paddingRight: "10px",
            color: "#333333",
            fontSize: "12px",
            fontFamily: "Roboto Condensed"
          }
        },
        scaleY2: {
          lineWidth: 0,
          values: rnR.min + ":" + rnR.max + ":" + rnR.step,
          format: "%v",
          guide: {
            lineStyle: "solid",
            lineColor: "#f5f5f5"
          },
          tick: {
            size: 0
          },
          item: {
            visible: true,
            paddingLeft: "10px",
            color: "#333333",
            fontSize: "12px",
            fontFamily: "Roboto Condensed"
          }
        },
        crosshairX: this.chart_settings.crosshair_settings
      };
    },
    graph_series() {
      var series = [];

      if (this.evolution.length === 0) return series;

      var br = this.chart_settings.globals.borderRadiusBars || "10px";

      series.push({
        type: "bar",
        stacked: true,
        values: this.rn_positive_values,
        scales: "scale-x,scale-y-2",
        text: this.$t("rn") + " (+)",
        backgroundColor: RN_GREEN,
        alpha: 0.85,
        borderWidth: 0,
        borderRadiusTopLeft: br,
        borderRadiusTopRight: br,
        borderRadiusBottomLeft: 0,
        borderRadiusBottomRight: 0
      });

      series.push({
        type: "bar",
        stacked: true,
        values: this.rn_negative_values,
        scales: "scale-x,scale-y-2",
        text: this.$t("rn") + " (-)",
        backgroundColor: RN_RED,
        alpha: 0.85,
        borderWidth: 0,
        borderRadiusBottomLeft: br,
        borderRadiusBottomRight: br,
        borderRadiusTopLeft: 0,
        borderRadiusTopRight: 0
      });

      series.push({
        type: "area",
        values: this.occupancy_mapped,
        scales: "scale-x,scale-y-2",
        text: this.$t("occupancy"),
        stacked: false,
        aspect: "spline",
        lineColor: "#5c6bc0",
        lineWidth: 1,
        backgroundColor: "#5c6bc0",
        alphaArea: 0.12,
        marker: {
          visible: false
        }
      });

      series.push({
        type: "line",
        values: this.prices,
        scales: "scale-x,scale-y",
        text: this.$t("pvp"),
        stacked: false,
        aspect: "stepped",
        lineColor: PRICE_LINE_NEUTRAL,
        lineWidth: "2px",
        styles: this.price_line_styles,
        marker: {
          visible: false
        }
      });

      return series;
    },
    ...mapState({
      chart_settings: state => state.chart_settings
    }),
    ...mapGetters({
      current_hotel: "current_hotel"
    })
  }
};
</script>

<style scoped>
.rms-day-price-chart-wrap {
  position: relative;
}

.rms-day-price-chart-current {
  position: absolute;
  top: 10px;
  left: 16px;
  z-index: 1;
  background: #ffffff;
  border: 1px solid #999999;
  border-radius: 3px;
  box-shadow: 0 2px 4px rgba(102, 102, 102, 0.3);
  padding: 5px 10px 10px 10px;
  font-family: "Roboto Condensed", sans-serif;
}

.rms-day-price-chart-current__label {
  font-size: 10px;
  line-height: 1.2;
}

.rms-day-price-chart-current__value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  color: #212121;
}
</style>
